import { inject } from 'inversify-props';
import IAuthService from "services/IAuthService";
import { IFormatVersion, ICampaign } from "@models";
import { AxiosRequestConfig } from "axios";
import { VideoFile } from '../../../adnami_ts_models/Models/File/VideoFile';


class AssetService {
    @inject() private authService: IAuthService;

    host: string = `${window.location.protocol}//${window.location.host}`;
    api: string = "/api/asset/";

    constructor(api: string = null) {
        if (api) this.api = api;
    }

    saveFormatImage(formatVersion: IFormatVersion, parameterId: string, fileToUpload: File) {
        return this._saveFormatAsset(
            formatVersion,
            fileToUpload,
            parameterId,
            "saveformatimage"
        );
    }

    saveFormatSeenThisAsset(formatVersion: IFormatVersion, fileToUpload: VideoFile, parameterId: string) {
        const endpoint = this.host + this.api + 'saveformatstream';
        let query = "";
        let urlParams = new URLSearchParams();
        urlParams.append("formatVersionId", formatVersion.id.toString());
        urlParams.append("parameterId", parameterId);
        urlParams.append("width", fileToUpload.videoOptions.width.toString());
        urlParams.append("height", fileToUpload.videoOptions.height.toString());
        query = "?" + urlParams.toString();

        // build form data
        const formData = new FormData();
        formData.append("file", fileToUpload);

        const requestConfig: AxiosRequestConfig = {
            headers: { "Content-Type": "multipart/form-data" }
        };

        const response = this.authService.post(endpoint + query, formData, requestConfig);
        return response;
    }

    saveFormatVideo(formatVersion: IFormatVersion, parameterId: string, fileToUpload: File) {
        return this._saveFormatAsset(
            formatVersion,
            fileToUpload,
            parameterId,
            "saveformatvideo"
        );
    }

    copyFormatAssets(fromFormatVersionId: string, toFormatVersionId: string) {
        const endpoint = this.host + this.api + "copyformatasset";

        let query = "";
        let urlParams = new URLSearchParams();
        urlParams.append("fromFormatVersionId", fromFormatVersionId);
        urlParams.append("toFormatVersionId", toFormatVersionId);
        query = "?" + urlParams.toString();

        const response = this.authService.post(
            endpoint + query,
            null
        );
        return response;
    }

    _saveFormatAsset(
        formatVersion: IFormatVersion,
        fileToUpload: File,
        parameterId: string,
        route: string
    ) {
        const endpoint = this.host + this.api + route;

        let query = "";
        let urlParams = new URLSearchParams();
        urlParams.append("formatVersionId", formatVersion.id);
        urlParams.append("parameterId", parameterId);
        urlParams.append("fileName", fileToUpload.name);
        query = "?" + urlParams.toString();

        // build form data
        const formData = new FormData();
        formData.append("file", fileToUpload);

        const requestConfig: AxiosRequestConfig = {
            headers: { "Content-Type": "multipart/form-data" }
        };

        const response = this.authService.post(
            endpoint + query,
            formData,
            requestConfig
        );
        return response;
    }

    copyCampaignAssets(fromCampaignId: number, toCampaignId: number) {
        const endpoint = this.host + this.api + "copycampaignassets";

        let query = "";
        let urlParams = new URLSearchParams();
        urlParams.append("fromCampaignId", fromCampaignId.toString());
        urlParams.append("toCampaignId", toCampaignId.toString());
        query = "?" + urlParams.toString();

        const response = this.authService.post(
            endpoint + query,
            null
        );
        return response;
    }

    saveCampaignImage(campaign: ICampaign, parameterId: string, fileToUpload: File, placeholderTag: string = "") {
        return this._saveCampaignAsset(campaign, parameterId, fileToUpload, "savecampaignimage", placeholderTag);
    }

    saveCampaignVideo(campaign: ICampaign, parameterId: string, fileToUpload: File, placeholderTag: string = "") {
        return this._saveCampaignAsset(campaign, parameterId, fileToUpload, "savecampaignvideo", placeholderTag);
    }

    saveCampaignSeenThisAsset(campaign: ICampaign, fileToUpload: VideoFile, parameterId: string, placeholderTag: string = "") {
        const endpoint = this.host + this.api + 'savecampaignstream';
        let query = "";
        let urlParams = new URLSearchParams();
        urlParams.append("campaignId", campaign.id.toString());
        urlParams.append("parameterId", parameterId);
        urlParams.append("width", fileToUpload.videoOptions.width.toString());
        urlParams.append("height", fileToUpload.videoOptions.height.toString());
        urlParams.append("placeholderTag", placeholderTag);
        query = "?" + urlParams.toString();

        // build form data
        const formData = new FormData();
        formData.append("file", fileToUpload);

        const requestConfig: AxiosRequestConfig = {
            headers: { "Content-Type": "multipart/form-data" }
        };

        const response = this.authService.post(endpoint + query, formData, requestConfig);
        return response;
    }

    updateSeenThisStatus(campaignId: string, parameterId: string, seenThisId: string, canvasParameterId: string) {
        const endpoint = this.host + this.api + 'updateseenthisstatus';
        let query = "";
        let urlParams = new URLSearchParams();
        urlParams.append("campaignId", campaignId);
        urlParams.append("parameterId", parameterId);
        urlParams.append("seenThisId", seenThisId);
        urlParams.append("canvasContentParameterID", canvasParameterId);
        
        query = "?" + urlParams.toString();
        
       
        const requestConfig: AxiosRequestConfig = {
            headers: { "Content-Type": "application/json" }
        };

        const response = this.authService.post(endpoint + query, requestConfig);
        return response;
    }

    updateformatparameterseenthisstatus(parameterId: string, seenThisId: string) {
        const endpoint = this.host + this.api + 'updateformatparameterseenthisstatus';
        let query = "";
        let urlParams = new URLSearchParams();
        urlParams.append("parameterId", parameterId);
        urlParams.append("seenThisId", seenThisId);

        query = "?" + urlParams.toString();

        const requestConfig: AxiosRequestConfig = {
            headers: { "Content-Type": "application/json" }
        };

        const response = this.authService.post(endpoint + query, requestConfig);
        return response;
    }

    _saveCampaignAsset(campaign: ICampaign, parameterId: string, fileToUpload: File, route: string, placeholderTag: string) {
        const endpoint = this.host + this.api + route;

        let query = "";
        let urlParams = new URLSearchParams();
        urlParams.append("campaignId", campaign.id.toString());
        urlParams.append("parameterId", parameterId);
        urlParams.append("placeholderTag", placeholderTag);
        query = "?" + urlParams.toString();

        // build form data
        const formData = new FormData();
        formData.append("file", fileToUpload);

        const requestConfig: AxiosRequestConfig = {
            headers: { "Content-Type": "multipart/form-data" }
        };

        const response = this.authService.post(
            endpoint + query,
            formData,
            requestConfig
        );
        return response;
    }
}

export default new AssetService();
